// Spec: viq-vault/.../specs/2026-06-10-discovery-admin-answer-viewing-design.md §5
import { useState } from 'react';
import { Button, Card, Descriptions, Result, Skeleton, Typography } from 'antd';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { EditOutlined } from '@ant-design/icons';
import { renderAnswerCell } from './discoveryCells';
import { ResponseEditModal, type DiscoveryAnswerBody } from './ResponseEditModal';
import type {
  DiscoveryQuestion,
  DiscoveryResponse,
  PersonDiscoveryAnswer,
} from './types';

export interface DiscoveryPanelProps {
  personId: string;
  /**
   * Shell-provided fetcher for one lead's Discovery questions + sparse answers,
   * plus any questions the lead answered at an earlier conference (`carryover`).
   * May return null; the panel then renders nothing.
   */
  fetchDiscovery: (personId: string) => Promise<DiscoveryResponse | null>;
  /**
   * Optional write closures. Absent → today's exact read-only render.
   *
   * Both are keyed by the QUESTION's own `conferenceId`, never the lead's
   * `person.conference_id`. After a lead move the answers stay bound to the
   * source conference's question copies, so the question is the only reliable
   * conference key.
   */
  updateAnswer?: (conferenceId: string, questionId: string, body: DiscoveryAnswerBody) => Promise<void>;
  deleteAnswer?: (conferenceId: string, questionId: string) => Promise<void>;
}

function isAnswered(answer: PersonDiscoveryAnswer | undefined): boolean {
  return (
    !!answer &&
    (answer.selectedOptionIds.length > 0 ||
      (answer.freeText != null && answer.freeText.trim() !== ''))
  );
}

/**
 * Read-only Discovery answers for one lead. Renders nothing when the lead's
 * conference has no Discovery question set and the lead has no non-empty
 * answers from another conference (mirrors the mobile fail-soft).
 * Error state shows a Result and does NOT crash the parent view.
 *
 * Answers carried over from a lead move render after the current set, one
 * section per source conference, and are never editable here.
 */
export function DiscoveryPanel({
  personId,
  fetchDiscovery,
  updateAnswer,
  deleteAnswer,
}: DiscoveryPanelProps) {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<DiscoveryQuestion | null>(null);
  const { data, isLoading, error } = useQuery({
    queryKey: ['leadDiscovery', personId],
    queryFn: () => fetchDiscovery(personId),
    enabled: !!personId,
  });

  if (isLoading) {
    return (
      <Card title="Discovery">
        <Skeleton active paragraph={{ rows: 3 }} />
      </Card>
    );
  }

  if (error) {
    return (
      <Card title="Discovery">
        <Result status="warning" title="Couldn't load Discovery answers" />
      </Card>
    );
  }

  if (!data) return null;

  const answersByQuestion = new Map<string, PersonDiscoveryAnswer>(
    data.answers.map((a) => [a.conferenceQuestionId, a]),
  );
  // Carried-over questions show only with a real answer: a cleared answer is
  // still a row, and under an "Answered at" heading it would read as one.
  const carryover = (data.carryover ?? [])
    .map((group) => ({
      ...group,
      questions: group.questions.filter((q) => isAnswered(answersByQuestion.get(q.id))),
    }))
    .filter((group) => group.questions.length > 0);
  if (data.questions.length === 0 && carryover.length === 0) return null;

  // Counted against the current questions only: `answers` also holds a moved
  // lead's carried-over answers, which belong to other conferences' sets.
  const answeredCount = data.questions.filter((q) => isAnswered(answersByQuestion.get(q.id))).length;

  return (
    <Card
      title="Discovery"
      extra={
        data.questions.length > 0 && (
          <Typography.Text type="secondary">
            {answeredCount} of {data.questions.length} answered
          </Typography.Text>
        )
      }
    >
      {data.questions.length > 0 && (
        <Descriptions column={1} colon={false} layout="vertical">
          {data.questions.map((q: DiscoveryQuestion) => (
            <Descriptions.Item
              key={q.id}
              label={
                <span>
                  <Typography.Text strong>{q.text}</Typography.Text>
                  {updateAnswer && (
                    <Button
                      type="text"
                      size="small"
                      icon={<EditOutlined />}
                      aria-label="Edit answer"
                      onClick={() => setEditing(q)}
                    />
                  )}
                </span>
              }
            >
              <Typography.Text>{renderAnswerCell(answersByQuestion.get(q.id), q)}</Typography.Text>
            </Descriptions.Item>
          ))}
        </Descriptions>
      )}
      {carryover.map((group, i) => (
        <Descriptions
          key={group.conferenceId}
          title={`Answered at ${group.conferenceName ?? 'another conference'}`}
          column={1}
          colon={false}
          layout="vertical"
          style={i > 0 || data.questions.length > 0 ? { marginTop: 24 } : undefined}
        >
          {group.questions.map((q) => (
            <Descriptions.Item key={q.id} label={<Typography.Text strong>{q.text}</Typography.Text>}>
              <Typography.Text>{renderAnswerCell(answersByQuestion.get(q.id), q)}</Typography.Text>
            </Descriptions.Item>
          ))}
        </Descriptions>
      ))}
      {editing && updateAnswer && (
        <ResponseEditModal
          key={`${personId}:${editing.id}`}
          open
          question={editing}
          answer={answersByQuestion.get(editing.id)}
          onSave={(body) => updateAnswer(editing.conferenceId, editing.id, body)}
          onDelete={deleteAnswer ? () => deleteAnswer(editing.conferenceId, editing.id) : undefined}
          onClose={() => setEditing(null)}
          onSaved={() => {
            void queryClient.invalidateQueries({ queryKey: ['leadDiscovery', personId] });
            // Company lead rules: an answer can meet (or, when deleted, un-meet) a
            // required question, and the lead's completeness rides on the lead
            // detail query (LeadDetailBody's ['leadDetail', id]; personId is that id).
            void queryClient.invalidateQueries({ queryKey: ['leadDetail', personId] });
          }}
        />
      )}
    </Card>
  );
}
